// Team-wide file search (shared/API.md §41.5): `POST /teams/{team}/files/search` answers
// `{order, file_infos, next_file_info_id}` (model.FileInfoList). These rules decode one page into
// result rows (newest first as the server orders them, deleted files out, duplicates across pages
// out) and decide whether another page may exist.

export interface TeamFileHit {
  fileId: string;
  postId: string;
  channelId: string;
  userId: string;
  name: string;
  extension: string;
  size: number;
  mimeType: string;
  width: number;
  height: number;
  createAt: number;
}

interface RawInfo {
  id?: string;
  post_id?: string;
  channel_id?: string;
  user_id?: string;
  name?: string;
  extension?: string;
  size?: number;
  mime_type?: string;
  width?: number;
  height?: number;
  create_at?: number;
  delete_at?: number;
}

export const FILE_SEARCH_PAGE = 60;

/** The request body (the contract's exact keys; the server's own modifiers ride inside `terms`). */
export function fileSearchBody(terms: string, page: number, timeZoneOffsetSeconds: number): Record<string, unknown> {
  return { terms: terms.trim(), is_or_search: false, time_zone_offset: timeZoneOffsetSeconds, page, per_page: FILE_SEARCH_PAGE, include_deleted_channels: false };
}

/**
 * One answer → rows in the server's order, skipping deleted infos and ids already in `seen` (the
 * server may ignore pagination and resend everything). `returned` is the raw count: a page shorter
 * than FILE_SEARCH_PAGE, or one that added nothing new, ends the list.
 */
export function decodeFileSearch(raw: unknown, seen: ReadonlySet<string>): { hits: TeamFileHit[]; returned: number; hasMore: boolean } {
  const o = raw && typeof raw === 'object' ? (raw as { order?: unknown; file_infos?: Record<string, RawInfo> }) : {};
  const order = Array.isArray(o.order) ? (o.order as unknown[]).filter((x): x is string => typeof x === 'string') : [];
  const infos = o.file_infos && typeof o.file_infos === 'object' ? o.file_infos : {};
  const hits: TeamFileHit[] = [];
  const local = new Set<string>();
  for (const id of order) {
    const f = infos[id];
    if (!f || f.delete_at || seen.has(id) || local.has(id)) continue;
    local.add(id);
    hits.push({
      fileId: id,
      postId: f.post_id ?? '',
      channelId: f.channel_id ?? '',
      userId: f.user_id ?? '',
      name: f.name ?? '',
      extension: f.extension ?? '',
      size: f.size ?? 0,
      mimeType: f.mime_type ?? '',
      width: f.width ?? 0,
      height: f.height ?? 0,
      createAt: f.create_at ?? 0,
    });
  }
  return { hits, returned: order.length, hasMore: order.length >= FILE_SEARCH_PAGE && hits.length > 0 };
}

/** The extension badge on a document tile: «PDF», «XLSX» (4 letters max), «FILE» without one. */
export function extensionBadge(name: string, extension: string): string {
  const ext = (extension || (name.includes('.') ? name.split('.').pop() ?? '' : '')).toUpperCase();
  return ext ? ext.slice(0, 4) : 'FILE';
}
